/**
 * Whether a score still matches the data it was computed from — gap **G9**, defect
 * **D-37**, on the screen of the person the number is about.
 *
 * Pure: no React, no clock, no request. The whole decision is one function over one
 * wire object, so every branch is drivable without mounting anything.
 *
 * ══════════════════════════════════════════════════════════════════════════════
 *  WHAT THIS IS NOT, BECAUSE FOUR NEIGHBOURING STATES ALREADY EXIST
 * ══════════════════════════════════════════════════════════════════════════════
 *
 * A stale score is **a real number that is out of date**. It is not any of:
 *
 *  - `confidence`, `data_completeness` and `missing_data_warnings` — those say what
 *    the scoring run could see **at the time it ran**, and are stored on the row. A
 *    stale score is very often fully complete and fully confident, because the
 *    commonest cause of staleness is the subject's own correction being approved.
 *  - the four `404` absence reasons — those mean there is **no number at all**.
 *  - `recalculated_count` — that counts recomputations that **happened**; this says
 *    whether one is **owed**.
 *
 * So it renders as its own note, beside an unchanged `score`, and never as an error
 * and never as missing data. `score` is not hidden, not zeroed and not annotated with
 * a question mark: the number is served rather than withheld precisely because the
 * employee it describes is entitled to argue with it, and a number they cannot see is
 * a number they cannot question.
 *
 * ══════════════════════════════════════════════════════════════════════════════
 *  `scheduled` AND `requires_request` ARE TWO DIFFERENT SENTENCES
 * ══════════════════════════════════════════════════════════════════════════════
 *
 * This is the distinction the whole module exists to keep, and it is the one a single
 * grey "đang cập nhật" badge would flatten:
 *
 *  - **`scheduled`** — the period is inside the window the hourly sweep scans. A run
 *    is coming and the number will fix itself. *Wait.*
 *  - **`requires_request`** — the period is **older than that window**. **No
 *    scheduled run will ever pick it up**, and the figure stays wrong until somebody
 *    calls `POST /api/v1/productivity/recalculate`. *Waiting is the wrong action.*
 *
 * Telling the second person to wait sends them to wait for something that will never
 * happen — about their own pay-adjacent number. So the two are separate variants of
 * this union, carry different severities and different icons, and are asserted apart
 * rather than asserted to both exist.
 *
 * ══════════════════════════════════════════════════════════════════════════════
 *  AND IT FAILS CLOSED
 * ══════════════════════════════════════════════════════════════════════════════
 *
 * `freshness` is `required` on the wire. A body without it is therefore a server this
 * client does not understand, **not** a settled score: reading an absence as `current`
 * is the defect itself rather than a smaller version of it, which is exactly what
 * `openapi.yaml` says about the field. So an absent or malformed object reads
 * `unknown`, and the screen says it cannot tell — the same direction `readingOf` takes
 * for a withheld number.
 *
 * A `state: 'stale'` with `rescore: 'not_needed'`, or a `current` carrying changed
 * inputs, are contradictions the server does not produce. They read `unknown` too,
 * for the same reason: guessing which half to believe would be this client inventing
 * an answer about somebody's score.
 */

export const CHANGED_INPUTS = [
  'algorithm_version',
  'daily_aggregate',
  'attendance_session',
  'time_entry',
] as const;

export type ChangedInput = (typeof CHANGED_INPUTS)[number];

export type FreshnessState = 'current' | 'stale';

export type RescoreDisposition = 'not_needed' | 'scheduled' | 'requires_request';

/** `ScoreFreshness` of `openapi.yaml`. Required on every score, list and detail. */
export interface ScoreFreshness {
  readonly state: FreshnessState;
  readonly changed_inputs: readonly ChangedInput[];
  readonly rescore: RescoreDisposition;
}

/**
 * What the panel renders.
 *
 * Four variants, not a boolean and a string: the two stale cases are different
 * **advice**, and a shape that made them one variant with a flag is a shape a
 * renderer can collapse by forgetting one branch.
 */
export type FreshnessReading =
  | { readonly kind: 'current' }
  | { readonly kind: 'stale-scheduled'; readonly changed: readonly ChangedInput[] }
  | { readonly kind: 'stale-manual'; readonly changed: readonly ChangedInput[] }
  | { readonly kind: 'unknown'; readonly reason: 'absent' | 'malformed' | 'contradictory' };

function isChangedInput(value: unknown): value is ChangedInput {
  return typeof value === 'string' && (CHANGED_INPUTS as readonly string[]).includes(value);
}

/**
 * Read the wire object, fail-closed.
 *
 * Takes `unknown` on purpose. The alternative — taking `ScoreFreshness | undefined` —
 * would let the type system promise a shape the wire cannot promise, and the one
 * thing this field must never do is read as "settled" when the server did not say so.
 */
export function readFreshness(value: unknown): FreshnessReading {
  if (value === undefined || value === null) return { kind: 'unknown', reason: 'absent' };
  if (typeof value !== 'object') return { kind: 'unknown', reason: 'malformed' };

  const { state, changed_inputs: inputs, rescore } = value as Partial<ScoreFreshness>;
  if (state !== 'current' && state !== 'stale') return { kind: 'unknown', reason: 'malformed' };
  if (!Array.isArray(inputs)) return { kind: 'unknown', reason: 'malformed' };
  if (rescore !== 'not_needed' && rescore !== 'scheduled' && rescore !== 'requires_request') {
    return { kind: 'unknown', reason: 'malformed' };
  }

  // An input name this build has no word for is kept out of the rendered list rather
  // than printed raw — but its presence still means something moved, so it cannot be
  // silently dropped either. `changed` may therefore be shorter than the wire's array
  // while the state stays `stale`, and the note says how many were named.
  const changed = inputs.filter(isChangedInput);

  if (state === 'current') {
    // `changed_inputs` is empty **exactly when** the state is `current`. A `current`
    // carrying one is a contradiction, and believing the `current` half would render
    // a moved score as settled.
    return inputs.length === 0 ? { kind: 'current' } : { kind: 'unknown', reason: 'contradictory' };
  }

  if (rescore === 'not_needed') return { kind: 'unknown', reason: 'contradictory' };
  return rescore === 'scheduled'
    ? { kind: 'stale-scheduled', changed }
    : { kind: 'stale-manual', changed };
}

/** Is this reading one the person has to act on themselves? */
export function needsSomebodyToAsk(reading: FreshnessReading): boolean {
  return reading.kind === 'stale-manual';
}
